import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useCreateEPIType, useUpdateEPIType, getEPICategoryLabel } from '@/hooks/useRPL';
import { epiTypeFormSchema, type EPITypeFormData } from '@/schemas/rplSchemas';
import type { EPIType, EPICategory } from '@/types/rpl';
import { Loader2 } from 'lucide-react';

interface EPITypeFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  epiType?: EPIType | null;
}

const CATEGORIES: EPICategory[] = ['head', 'hands', 'eyes', 'feet', 'body', 'respiratory', 'hearing'];

export function EPITypeFormDialog({ open, onOpenChange, epiType }: EPITypeFormDialogProps) {
  const createMutation = useCreateEPIType();
  const updateMutation = useUpdateEPIType();
  const [loading, setLoading] = useState(false);

  const form = useForm<EPITypeFormData>({
    resolver: zodResolver(epiTypeFormSchema),
    defaultValues: {
      name: '',
      code: '',
      category: undefined,
      description: '',
      lifespan_months: null,
      replacement_alert_days: 30,
      requires_certification: false,
      image_url: '',
      is_active: true,
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        name: epiType?.name || '',
        code: epiType?.code || '',
        category: epiType?.category || undefined,
        description: epiType?.description || '',
        lifespan_months: epiType?.lifespan_months || null,
        replacement_alert_days: epiType?.replacement_alert_days || 30,
        requires_certification: epiType?.requires_certification || false,
        image_url: epiType?.image_url || '',
        is_active: epiType?.is_active ?? true,
      });
    }
  }, [open, epiType, form]);

  const onSubmit = async (data: EPITypeFormData) => {
    setLoading(true);
    try {
      const payload = {
        name: data.name,
        code: data.code || null,
        category: data.category || null,
        description: data.description || null,
        lifespan_months: data.lifespan_months || null,
        replacement_alert_days: data.replacement_alert_days,
        requires_certification: data.requires_certification,
        image_url: data.image_url || null,
        is_active: data.is_active,
      };

      if (epiType) {
        await updateMutation.mutateAsync({ id: epiType.id, data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }

      onOpenChange(false);
      form.reset();
    } catch (error) {
      // Error handled by mutation
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{epiType ? 'Editar Tipo de EPI' : 'Nuevo Tipo de EPI'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>Nombre *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: Casco de Seguridad" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Codigo</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: EPI-CASCO" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Categoria</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value || ''}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {CATEGORIES.map((cat) => (
                          <SelectItem key={cat} value={cat}>
                            {getEPICategoryLabel(cat)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lifespan_months"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Vida util (meses)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="replacement_alert_days"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Dias alerta caducidad</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descripcion</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={3} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Requiere certificacion</FormLabel>
                <p className="text-sm text-muted-foreground">El EPI requiere formacion especifica</p>
              </div>
              <FormField
                control={form.control}
                name="requires_certification"
                render={({ field }) => (
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                )}
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Activo</FormLabel>
                <p className="text-sm text-muted-foreground">Disponible para asignacion</p>
              </div>
              <FormField
                control={form.control}
                name="is_active"
                render={({ field }) => (
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                )}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {epiType ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
